import { type AppNode } from '../../types';
import { getNodeDimensions } from '../nodeDimensions';

const GROUPABLE_NODE_TYPES = new Set(['image', 'video', 'text']);

export function isGroupableNode(node: AppNode): boolean {
  return GROUPABLE_NODE_TYPES.has(node.type ?? '');
}

export function getGroupMembers(nodes: AppNode[], groupId: string): AppNode[] {
  return nodes.filter((n) => n.groupId === groupId);
}

export function getGroupIds(nodes: AppNode[]): Set<string> {
  const ids = new Set<string>();

  for (const node of nodes) {
    if (node.groupId) {
      ids.add(node.groupId);
    }
  }

  return ids;
}

export type GroupBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
};

export function computeGroupBounds(members: AppNode[]): GroupBounds | undefined {
  if (members.length === 0) {
    return undefined;
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const node of members) {
    const { width, height } = getNodeDimensions(node);

    minX = Math.min(minX, node.position.x);
    minY = Math.min(minY, node.position.y);
    maxX = Math.max(maxX, node.position.x + width);
    maxY = Math.max(maxY, node.position.y + height);
  }

  return {
    height: maxY - minY,
    maxX,
    maxY,
    minX,
    minY,
    width: maxX - minX,
  };
}

export type GroupScreenBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export function computeGroupScreenBounds(
  members: AppNode[],
  panX: number,
  panY: number,
  zoom: number,
): GroupScreenBounds | undefined {
  const flowBounds = computeGroupBounds(members);

  if (!flowBounds) {
    return undefined;
  }

  return {
    height: flowBounds.height * zoom,
    left: flowBounds.minX * zoom + panX,
    top: flowBounds.minY * zoom + panY,
    width: flowBounds.width * zoom,
  };
}

export function canGroupSelection(selectedNodes: AppNode[]): boolean {
  const groupableNodes = selectedNodes.filter(isGroupableNode);

  return groupableNodes.length >= 2 && groupableNodes.every((n) => !n.groupId);
}

// Falls back to 0 for undefined zIndex because nodes without an explicit
// zIndex are rendered at the bottom of the stack by React Flow.
export function getGroupMaxZIndex(nodes: Pick<AppNode, 'groupId' | 'zIndex'>[], groupId: string): number {
  let max = -Infinity;

  for (const node of nodes) {
    if (node.groupId === groupId) {
      max = Math.max(max, node.zIndex ?? 0);
    }
  }

  return max === -Infinity ? 0 : max;
}
